import React, { forwardRef } from 'react';
import { EditorProvider } from '@tiptap/react';
import { isEmpty, noop } from 'lodash';

import type { onUploadFinishFunc } from '~/components/core/Templates/UploadImageDialog';
import cn from '~/Utils/cn';

import { getExtensions } from './extensions/config';
import { ModalLinkProvider } from './MenuBar/link/LinkModalContext';
import type { PartialEditor } from './EditorContentComponent';
import EditorContentComponent from './EditorContentComponent';
import MenuBar from './MenuBar';
import type { DisplayConfigProps, SuggestionOption } from './types';

// Stable identity so an editor that configures no tokens doesn't rebuild the link modal's context
// value on every render.
const NO_SUGGESTION_OPTIONS: SuggestionOption[] = [];

export interface EditorProps {
  content?: string;
  className?: string;
  containerClassName?: string;
  suggestionOptions?: SuggestionOption[];
  displayConfig?: DisplayConfigProps;
  onChange?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  onUploadFinish?: onUploadFinishFunc;
  saveAsMarkdown?: boolean;
}

const Editor = forwardRef<PartialEditor, EditorProps>(
  (
    {
      content = '',
      suggestionOptions = NO_SUGGESTION_OPTIONS,
      displayConfig = {},
      onChange = noop,
      disabled,
      className,
      containerClassName,
      autoFocus,
      onUploadFinish,
      saveAsMarkdown = false,
    }: EditorProps,
    ref
  ) => {
    return (
      <div className={cn('rounded-md border-1 border-solid border-slate-600', containerClassName)}>
        <ModalLinkProvider tokenOptions={displayConfig.suggestions ? suggestionOptions : NO_SUGGESTION_OPTIONS}>
          <EditorProvider
            editable={!disabled}
            extensions={getExtensions(suggestionOptions, displayConfig, disabled, saveAsMarkdown)}
            editorProps={{
              attributes: {
                class: cn('px-20 py-12 outline-none', className),
              },
            }}
            parseOptions={{
              // The backend converts whitespace to HTML entities (<br>, &nbsp;) for email compatibility.
              // Setting preserveWhitespace to 'full' prevents the editor from collapsing the provided content's whitespace and
              // newline characters into a single space, making the display more consistent with how it appears on external platforms.
              preserveWhitespace: 'full',
            }}
            autofocus={autoFocus}
            content={content}
            onUpdate={({ editor }) => {
              const value = saveAsMarkdown ? (editor.storage.markdown?.getMarkdown?.() ?? '') : editor.getHTML();
              onChange(value);
            }}
            slotBefore={
              !isEmpty(displayConfig) ? (
                <MenuBar
                  suggestionOptions={suggestionOptions}
                  displayConfig={displayConfig}
                  onUploadFinish={onUploadFinish}
                />
              ) : null
            }
          >
            <EditorContentComponent ref={ref} />
          </EditorProvider>
        </ModalLinkProvider>
      </div>
    );
  }
);

Editor.displayName = 'Editor';

export default Editor;
